import React, { useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import { useNavigate, useParams } from "react-router-dom";
import IOrder from "../../../models/IOrder";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetOrderDTO from "../../../models/DTO/IGetOrderDTO";
import IOnGetOrderDTO from "../../../models/DTO/IOnGetOrderDTO";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import UserService from "../../../services/UserService";
import OwButton from "../../shared/OwButton/OwButton";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import { EcommercePaymentSummary, getSummarizedPayments } from "../EcommercePaymentSummary/EcommercePaymentSummary";
import { NumericFormat } from "react-number-format";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import OwPrintButton from "../../shared/OwPrintButton/OwPrintButton";
import UIService from "../../../services/UIService";
import { Socket } from "socket.io-client";
import { QtyDetail } from "../../shared/QtyDetail/QtyDetail";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import HelperService from "../../../services/HelperService";
import { KitItemsDisplay } from "../../shared/KitItemsDisplay/KitItemsDisplay";
import { showsDeliveryDate } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDateRulesUtils";
import { DocLogo } from "../../shared/DocLogo/DocLogo";
import "./EcommerceInvoice.scss";

type Props = {};

export const EcommerceInvoice: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    const params = useParams();
    const history = useNavigate();

    // @ts-expect-error
    const { guid } = params;

    const navigate = useNavigate();

    const goBack = () => {
        navigate(-1);
    };

    const [order, setOrder] = useState<IOrder>();

    const onGetOrder = (data: IOnGetOrderDTO) => {
        setOrder(data.order);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getOrder();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getOrder = () => {
        if (guid) {
            const payload: IGetOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                orderGuid: guid,
            };

            socket.emit(EndpointsEnum.OrderGetOrder, payload);
        }
    };

    const formatMoney = (value?: number | null) =>
        HelperService.formatCurrency({
            value: value || 0,
            currency: "USD",
            precision: supplierModel.pricing_precision || 2,
        });

    const sellerAddress = supplierModel.Bill_To || supplierModel.Ship_To;

    return (
        <div className={"EcommerceInvoice"}>
            {!order ? (
                <div className={"invoice-loading"}>
                    <ActivityIndicator active={true} />
                    <OwLabel title={"loading"} noBottomMargin={true} />
                </div>
            ) : (
                <>
                    <div className={"invoice-toolbar d-print-none"}>
                        <OwButton title={"back"} className={"invoice-btn-secondary"} leftIconClass={"fas fa-arrow-left"} fn={goBack} />
                        <OwPrintButton className={"invoice-btn-primary"} />
                    </div>

                    <div className={"invoice-sheet"}>
                        <div className={"invoice-header"}>
                            <div className={"invoice-seller"}>
                                <DocLogo height={"auto"} maxWidth={"14rem"} maxHeight={"5rem"} />
                                <div className={"invoice-seller-name"}>{supplierModel.name}</div>
                                {sellerAddress ? <AddressDisplay address={sellerAddress} /> : null}
                                {supplierModel.phone ? <div>{supplierModel.phone}</div> : null}
                            </div>

                            <div className={"invoice-heading"}>
                                <OwTitle title={"invoice"} tag={"h2"} className={"invoice-title"} />
                                <dl className={"invoice-facts"}>
                                    <div>
                                        <dt>
                                            <OwLabel title={"order_no"} noBottomMargin={true} />
                                        </dt>
                                        <dd>{order.name}</dd>
                                    </div>
                                    <div>
                                        <dt>
                                            <OwLabel title={"invoice_date"} noBottomMargin={true} />
                                        </dt>
                                        <dd>{UserService.parseDate(order.submit_date || order.created_at)}</dd>
                                    </div>
                                    {order.po_no ? (
                                        <div>
                                            <dt>
                                                <OwLabel title={"po_no"} noBottomMargin={true} />
                                            </dt>
                                            <dd>{order.po_no}</dd>
                                        </div>
                                    ) : null}
                                    {showsDeliveryDate(order, supplierModel) ? (
                                        <div>
                                            <dt>
                                                <OwLabel title={"delivery_date"} noBottomMargin={true} />
                                            </dt>
                                            <dd>{UserService.parseUTCDate(order.delivery_date)}</dd>
                                        </div>
                                    ) : null}
                                    {supplierModel.has_order_due_date && order.due_date ? (
                                        <div>
                                            <dt>
                                                <OwLabel title={"due_date"} noBottomMargin={true} />
                                            </dt>
                                            <dd>{UserService.parseDate(order.due_date)}</dd>
                                        </div>
                                    ) : null}
                                </dl>
                            </div>
                        </div>

                        <div className={"invoice-parties"}>
                            {order.Vendor?.Bill_To ? (
                                <div>
                                    <OwLabel title={"bill_to"} className={"invoice-label"} />
                                    <AddressDisplay address={order.Vendor.Bill_To} showName={true} showCompanyName={true} />
                                </div>
                            ) : null}
                            <div>
                                <OwLabel title={"ship_to"} className={"invoice-label"} />
                                <AddressDisplay address={order.Ship_To} showName={true} showCompanyName={true} />
                            </div>
                        </div>

                        <table className={"invoice-items"}>
                            <thead>
                                <tr>
                                    <th>
                                        <OwLabel title={"item"} noBottomMargin={true} />
                                    </th>
                                    <th className={"is-number"}>
                                        <OwLabel title={"qty"} noBottomMargin={true} />
                                    </th>
                                    {!hidePricing ? (
                                        <>
                                            <th className={"is-number"}>
                                                <OwLabel title={"unit_price"} noBottomMargin={true} />
                                            </th>
                                            <th className={"is-number"}>
                                                <OwLabel title={"total"} noBottomMargin={true} />
                                            </th>
                                        </>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody>
                                {order.Order_Items.map((orderItem) => (
                                    <tr key={orderItem.guid}>
                                        <td>
                                            <div className={"invoice-item-name"}>{orderItem.Catalog_Item?.name}</div>
                                            {orderItem.Catalog_Item?.sku ? (
                                                <div className={"invoice-item-sku"}>
                                                    <OwLabel title={"sku"} noBottomMargin={true} /> {orderItem.Catalog_Item.sku}
                                                </div>
                                            ) : null}
                                            <KitItemsDisplay orderItem={orderItem} />
                                        </td>
                                        <td className={"is-number"}>
                                            <QtyDetail item={orderItem} />
                                        </td>
                                        {!hidePricing ? (
                                            <>
                                                <td className={"is-number"}>{formatMoney(orderItem.cost_price_frozen)}</td>
                                                <td className={"is-number"}>
                                                    {formatMoney(orderItem.total || (orderItem.cost_price_frozen || 0) * orderItem.qty)}
                                                </td>
                                            </>
                                        ) : null}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className={"invoice-summary"}>
                            <div className={"invoice-summary-notes"}>
                                {getSummarizedPayments(order.Payments).length ? (
                                    <div>
                                        <OwLabel title={"payment"} className={"invoice-label"} />
                                        <EcommercePaymentSummary
                                            payments={order.Payments}
                                            hidePricing={hidePricing}
                                            pricingPrecision={supplierModel.pricing_precision}
                                        />
                                    </div>
                                ) : null}
                                {order.note ? (
                                    <div className={"invoice-note"}>
                                        <OwLabel title={"note"} className={"invoice-label"} />
                                        <div>{order.note}</div>
                                    </div>
                                ) : null}
                            </div>

                            {!hidePricing ? (
                                <div className={"invoice-totals"}>
                                    <div className={"invoice-total-row"}>
                                        <OwLabel title={"subtotal"} noBottomMargin={true} />
                                        <span>{formatMoney(order.sub_total)}</span>
                                    </div>
                                    {order.discount_total && order.discount_total > 0 ? (
                                        <div className={"invoice-total-row"}>
                                            <OwLabel title={"discount"} noBottomMargin={true} />
                                            <span>-{formatMoney(order.discount_total)}</span>
                                        </div>
                                    ) : null}
                                    {order.promo_code && order.promo_discount_total && order.promo_discount_total > 0 ? (
                                        <div className={"invoice-total-row"} data-testid={"invoice-promo"}>
                                            <span>
                                                <OwLabel title={"promo_code"} noBottomMargin={true} /> ({order.promo_code})
                                            </span>
                                            <span>-{formatMoney(order.promo_discount_total)}</span>
                                        </div>
                                    ) : null}
                                    <div className={"invoice-total-row"}>
                                        <OwLabel title={"shipping"} noBottomMargin={true} />
                                        <span>{formatMoney(order.shipping)}</span>
                                    </div>
                                    <div className={"invoice-total-row"}>
                                        <OwLabel title={"tax"} noBottomMargin={true} />
                                        <span>{formatMoney(order.tax)}</span>
                                    </div>
                                    <div className={"invoice-total-row is-grand-total"}>
                                        <OwLabel title={"grand_total"} noBottomMargin={true} />
                                        <span>{formatMoney(order.grand_total)}</span>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
